import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Wrench, Plus, Trash2, Search, ArrowLeftRight, Undo2, Loader2, Warehouse, PackageCheck, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { LookupManager } from "../components/LookupManager";

const DEFAULT_CATEGORIES = ["BASIC INSTRUMENTS", "SURGICAL INSTRUMENTS", "PROSTHO INSTRUMENTS", "ENDO INSTRUMENTS", "PEDO INSTRUMENTS", "ORTHO INSTRUMENTS", "HYGIENE INSTRUMENTS", "IPR SET AND HANDPIECE WITH GAUGE", "OTHER"];

const locLabel = (l) => (l === "CSSD" ? "CSSD Warehouse" : `Clinic ${String(l).replace("CL", "")}`);

export default function InstrumentsInventory() {
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const canManage = ["admin", "sterilization_storage"].includes(user?.role);
  const canDeliver = ["admin", "sterilization_storage"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [loans, setLoans] = useState([]);
  const [locations, setLocations] = useState([]);
  const [requests, setRequests] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);

  const loadCategories = () => axios.get(`${API}/lookups/instrument_categories`, { headers })
    .then(r => { if (r.data?.length) setCategories(r.data.map(c => c.label)); }).catch(() => {});
  useEffect(() => { loadCategories(); }, []);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [loc, setLoc] = useState("CSSD");
  const [catFilter, setCatFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", category: "OTHER", qty: "" });
  const [borrowItem, setBorrowItem] = useState(null);
  const [borrowForm, setBorrowForm] = useState({ clinic: "", qty: "1", notes: "" });
  const [editQty, setEditQty] = useState(null);

  const load = async () => {
    try {
      const [ri, rl, rloc, rreq] = await Promise.all([
        axios.get(`${API}/instruments`, { headers }),
        axios.get(`${API}/instruments/loans/all`, { headers }),
        axios.get(`${API}/instruments/locations`, { headers }),
        canManage ? axios.get(`${API}/instruments/requests/all`, { headers }) : Promise.resolve({ data: [] }),
      ]);
      setItems(ri.data);
      setLoans(rl.data);
      setLocations(rloc.data);
      setRequests(rreq.data);
    } catch { toast.error("Failed to load inventory"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => items.filter(it =>
    (!search || it.name.toLowerCase().includes(search.toLowerCase())) &&
    (catFilter === "all" || it.category === catFilter) &&
    (loc === "all" || (it.stock?.[loc] || 0) > 0 || loc === "CSSD")
  ), [items, search, catFilter, loc]);

  const saveQty = async (it, value) => {
    const v = parseInt(value, 10);
    if (isNaN(v) || v < 0) { toast.error("Invalid quantity"); return; }
    const stock = { ...(it.stock || {}) };
    if (v === 0) delete stock[loc]; else stock[loc] = v;
    try {
      await axios.put(`${API}/instruments/${it.id}`, { stock }, { headers });
      toast.success("Quantity updated");
      setEditQty(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const addInstrument = async () => {
    if (!addForm.name.trim()) { toast.error("Instrument name is required"); return; }
    const stock = {};
    const q = parseInt(addForm.qty, 10);
    if (!isNaN(q) && q > 0) stock[loc === "all" ? "CSSD" : loc] = q;
    try {
      await axios.post(`${API}/instruments`, { name: addForm.name, category: addForm.category, stock }, { headers });
      toast.success("Instrument added");
      setAddOpen(false); setAddForm({ name: "", category: "OTHER", qty: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
  };

  const deleteInstrument = async (it) => {
    if (!window.confirm(`Permanently delete "${it.name}" from inventory?`)) return;
    try {
      await axios.delete(`${API}/instruments/${it.id}`, { headers });
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const submitBorrow = async () => {
    if (!borrowForm.clinic) { toast.error("Select a clinic"); return; }
    try {
      await axios.post(`${API}/instruments/${borrowItem.id}/borrow`,
        { clinic: borrowForm.clinic, qty: parseInt(borrowForm.qty, 10) || 1, notes: borrowForm.notes }, { headers });
      toast.success("Loan recorded from CSSD warehouse");
      setBorrowItem(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Borrow failed"); }
  };

  const returnLoan = async (loan) => {
    try {
      await axios.post(`${API}/instruments/loans/${loan.id}/return`, {}, { headers });
      toast.success("Instrument returned to warehouse");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Return failed"); }
  };

  const activeLoans = loans.filter(l => l.status === "borrowed");
  const overdueLoans = activeLoans.filter(l => l.overdue);
  const pendingRequests = requests.filter(r => r.status === "pending");

  const [deliverReq, setDeliverReq] = useState(null);
  const [deliverPin, setDeliverPin] = useState("");
  const [deliverError, setDeliverError] = useState("");
  const [delivering, setDelivering] = useState(false);

  const submitDeliver = async () => {
    if ((deliverPin || "").trim().length < 4) { setDeliverError("Enter the 4-digit PIN shown on the clinic's request screen"); return; }
    setDelivering(true); setDeliverError("");
    try {
      await axios.post(`${API}/instruments/requests/${deliverReq.id}/deliver`, { pin: deliverPin.trim() }, { headers });
      toast.success("Delivered — items moved to clinic custody");
      setDeliverReq(null); setDeliverPin("");
      load();
    } catch (e) {
      setDeliverError(e.response?.data?.detail || "Delivery failed");
    } finally { setDelivering(false); }
  };

  const rejectRequest = async (req) => {
    const reason = window.prompt("Rejection reason (optional):") ?? null;
    if (reason === null) return;
    try {
      await axios.post(`${API}/instruments/requests/${req.id}/reject`, { reason }, { headers });
      toast.success("Request rejected");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#655dc6]" /></div>;

  return (
    <div dir="ltr" className="space-y-5 text-left" data-testid="instruments-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#655dc6] flex items-center gap-2"><Wrench className="w-7 h-7" /> Instruments Inventory</h1>
        <p className="text-slate-500 mt-1 text-sm">{items.length} instruments — delivering a request moves items into the clinic's own stock (custody). Sending them to sterilization deducts the clinic stock, and once they reach the Sterile Area they return to CSSD and become available again</p>
      </div>

      {overdueLoans.length > 0 && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3" data-testid="overdue-banner">
          <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
          <p className="text-sm text-red-700 font-medium">
            {overdueLoans.length} borrowed instrument{overdueLoans.length === 1 ? "" : "s"} overdue for more than 10 days — check the Loans tab
          </p>
        </div>
      )}

      <Tabs defaultValue="inventory" dir="ltr">
        <TabsList>
          <TabsTrigger value="inventory" data-testid="tab-inventory">Inventory</TabsTrigger>
          <TabsTrigger value="loans" data-testid="tab-loans">
            Loans ({activeLoans.length}) {overdueLoans.length > 0 && <Badge className="ml-1.5 bg-red-500 text-white hover:bg-red-500 h-5 px-1.5">{overdueLoans.length} overdue</Badge>}
          </TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests">
            Clinic Requests {pendingRequests.length > 0 && <Badge className="ml-1.5 bg-red-500 text-white hover:bg-red-500 h-5 px-1.5">{pendingRequests.length}</Badge>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inventory" className="space-y-4 mt-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search instruments..." className="pl-10" data-testid="inst-search" />
            </div>
            <Select value={loc} onValueChange={setLoc}>
              <SelectTrigger className="w-44" data-testid="inst-loc"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="CSSD">CSSD Warehouse</SelectItem>
                {locations.map(l => <SelectItem key={l.code} value={l.code}>{locLabel(l.code)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={catFilter} onValueChange={setCatFilter}>
              <SelectTrigger className="w-48" data-testid="inst-cat"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            {user?.role === "admin" && (
              <LookupManager lookupKey="instrument_categories" title="Manage Categories" token={token} onChanged={loadCategories} placeholder="New category name..." />
            )}
            {canManage && (
              <Button onClick={() => setAddOpen(true)} className="bg-[#655dc6] hover:bg-[#5449a8] text-white gap-1.5" data-testid="add-instrument-btn">
                <Plus className="w-4 h-4" /> Add Instrument
              </Button>
            )}
          </div>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-left">Instrument</TableHead>
                    <TableHead className="text-left">Category</TableHead>
                    <TableHead className="text-center">Qty in {locLabel(loc)}</TableHead>
                    <TableHead className="text-center">CSSD Available</TableHead>
                    <TableHead className="text-center">On Loan</TableHead>
                    <TableHead className="text-center">Total</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(it => (
                    <TableRow key={it.id} data-testid={`inst-row-${it.id}`}>
                      <TableCell className="font-medium text-xs">{it.name}</TableCell>
                      <TableCell className="text-[10px] text-slate-500">{it.category}</TableCell>
                      <TableCell className="text-center">
                        {editQty === it.id ? (
                          <Input
                            autoFocus type="number" min="0" defaultValue={it.stock?.[loc] || 0}
                            className="w-20 h-8 text-center mx-auto"
                            onBlur={e => saveQty(it, e.target.value)}
                            onKeyDown={e => e.key === "Enter" && saveQty(it, e.target.value)}
                            data-testid={`qty-input-${it.id}`}
                          />
                        ) : (() => {
                          const shown = it.stock?.[loc] || 0;
                          return (
                            <button
                              className={`font-bold ${canManage ? "underline decoration-dotted cursor-pointer" : ""} ${shown > 0 ? "text-[#655dc6]" : "text-slate-300"}`}
                              onClick={() => canManage && setEditQty(it.id)}
                              data-testid={`qty-${it.id}`}
                            >
                              {shown}
                            </button>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="text-center text-sm">{it.cssd_available}</TableCell>
                      <TableCell className="text-center">
                        {it.on_loan > 0 ? <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">{it.on_loan}</Badge> : <span className="text-slate-300">—</span>}
                      </TableCell>
                      <TableCell className="text-center font-semibold">{it.total}</TableCell>
                      <TableCell className="text-center">
                        {canManage && (
                          <div className="flex items-center justify-center gap-1">
                            <Button size="sm" variant="ghost" disabled={it.cssd_available < 1}
                              onClick={() => { setBorrowItem(it); setBorrowForm({ clinic: "", qty: "1", notes: "" }); }}
                              title="Loan from CSSD to a clinic" className="text-sky-600 hover:bg-sky-50 h-7"
                              data-testid={`borrow-${it.id}`}>
                              <ArrowLeftRight className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => deleteInstrument(it)} className="text-red-400 hover:bg-red-50 h-7" data-testid={`del-${it.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {filtered.length === 0 && <p className="text-center text-sm text-slate-400 py-10">No matching instruments</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="loans" className="space-y-4 mt-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-left">Instrument</TableHead>
                    <TableHead className="text-left">To</TableHead>
                    <TableHead className="text-center">Qty</TableHead>
                    <TableHead className="text-left">By</TableHead>
                    <TableHead className="text-left">Borrowed On</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-center">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loans.map(l => (
                    <TableRow key={l.id} className={l.overdue ? "bg-red-50 hover:bg-red-50" : ""} data-testid={`loan-row-${l.id}`}>
                      <TableCell className="text-xs font-medium">{l.instrument_name}</TableCell>
                      <TableCell className="text-sm">{locLabel(l.clinic)}</TableCell>
                      <TableCell className="text-center font-bold">{l.qty}</TableCell>
                      <TableCell className="text-xs text-slate-500">{l.borrowed_by_name}</TableCell>
                      <TableCell className="text-xs font-mono">{(l.borrowed_at || "").slice(0, 10)}</TableCell>
                      <TableCell className="text-center">
                        {l.status === "borrowed"
                          ? (l.overdue
                            ? <Badge className="bg-red-500 text-white hover:bg-red-500 gap-1"><AlertTriangle className="w-3 h-3" /> Overdue {l.days_out}d</Badge>
                            : <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">On loan{typeof l.days_out === "number" ? ` — ${l.days_out}d` : ""}</Badge>)
                          : <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Returned {(l.returned_at || "").slice(0, 10)}</Badge>}
                      </TableCell>
                      <TableCell className="text-center">
                        {l.status === "borrowed" && canManage && (
                          <Button size="sm" variant="outline" onClick={() => returnLoan(l)} className="h-7 text-xs gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`return-${l.id}`}>
                            <Undo2 className="w-3.5 h-3.5" /> Return
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {loans.length === 0 && <p className="text-center text-sm text-slate-400 py-10">No loans yet</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="requests" className="space-y-4 mt-4">
          <p className="text-xs text-slate-500">Requests received when scanning the clinic QR and choosing "Request Instruments" — delivery is done by the Sterile Storage account or admin</p>
          <div className="space-y-3">
            {requests.map(r => (
              <Card key={r.id} className="border-0 shadow-sm" data-testid={`req-card-${r.id}`}>
                <CardContent className="py-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-[#655dc6]">{locLabel(r.clinic_code || r.clinic_name)}</span>
                        {r.status === "pending" && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Pending delivery</Badge>}
                        {r.status === "delivered" && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Delivered</Badge>}
                        {r.status === "rejected" && <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Rejected</Badge>}
                      </div>
                      <p className="text-xs text-slate-500">Requested by: {r.requester_name} — {(r.created_at || "").slice(0, 16).replace("T", " ")}</p>
                      {r.notes && <p className="text-xs text-slate-400">Notes: {r.notes}</p>}
                      {r.status === "delivered" && <p className="text-[11px] text-emerald-600">Delivered by: {r.delivered_by_name} — {(r.delivered_at || "").slice(0, 16).replace("T", " ")}</p>}
                      {r.status === "rejected" && r.reject_reason && <p className="text-[11px] text-red-500">Reason: {r.reject_reason}</p>}
                    </div>
                    {r.status === "pending" && canDeliver && (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => { setDeliverReq(r); setDeliverPin(""); setDeliverError(""); }} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1 h-8" data-testid={`deliver-${r.id}`}>
                          <PackageCheck className="w-4 h-4" /> Deliver
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => rejectRequest(r)} className="border-red-300 text-red-600 hover:bg-red-50 gap-1 h-8" data-testid={`reject-${r.id}`}>
                          <X className="w-4 h-4" /> Reject
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {r.items.map((it, i) => (
                      <Badge key={i} variant="outline" className="text-[10px] font-normal">{it.instrument_name} × {it.qty}</Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
            {requests.length === 0 && <p className="text-center text-sm text-slate-400 py-10">No requests yet — they arrive when a clinic scans its QR and picks "Request Instruments"</p>}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add instrument */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent dir="ltr" className="max-w-md">
          <DialogHeader><DialogTitle>Add New Instrument</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Instrument name *</Label>
              <Input value={addForm.name} onChange={e => setAddForm({ ...addForm, name: e.target.value })} data-testid="add-inst-name" />
            </div>
            <div className="space-y-1">
              <Label>Category</Label>
              <Select value={addForm.category} onValueChange={v => setAddForm({ ...addForm, category: v })}>
                <SelectTrigger data-testid="add-inst-cat"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Qty in {locLabel(loc === "all" ? "CSSD" : loc)}</Label>
              <Input type="number" min="0" value={addForm.qty} onChange={e => setAddForm({ ...addForm, qty: e.target.value })} data-testid="add-inst-qty" />
            </div>
            <Button onClick={addInstrument} className="w-full bg-[#655dc6] hover:bg-[#5449a8] text-white" data-testid="add-inst-submit">Add</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Borrow */}
      <Dialog open={!!borrowItem} onOpenChange={o => !o && setBorrowItem(null)}>
        <DialogContent dir="ltr" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Warehouse className="w-5 h-5 text-sky-600" /> Loan — {borrowItem?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Available in CSSD warehouse: <b>{borrowItem?.cssd_available}</b></p>
            <div className="space-y-1">
              <Label>To clinic *</Label>
              <Select value={borrowForm.clinic} onValueChange={v => setBorrowForm({ ...borrowForm, clinic: v })}>
                <SelectTrigger data-testid="borrow-clinic"><SelectValue placeholder="Select clinic" /></SelectTrigger>
                <SelectContent>{locations.map(l => <SelectItem key={l.code} value={l.code}>{locLabel(l.code)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Qty</Label>
              <Input type="number" min="1" max={borrowItem?.cssd_available || 1} value={borrowForm.qty} onChange={e => setBorrowForm({ ...borrowForm, qty: e.target.value })} data-testid="borrow-qty" />
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Input value={borrowForm.notes} onChange={e => setBorrowForm({ ...borrowForm, notes: e.target.value })} placeholder="Optional" data-testid="borrow-notes" />
            </div>
            <Button onClick={submitBorrow} className="w-full bg-[#655dc6] hover:bg-[#5449a8] text-white" data-testid="borrow-submit">Record Loan</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Deliver with PIN */}
      <Dialog open={!!deliverReq} onOpenChange={o => { if (!o) { setDeliverReq(null); setDeliverError(""); } }}>
        <DialogContent dir="ltr" className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PackageCheck className="w-5 h-5 text-emerald-600" /> Deliver — {deliverReq && locLabel(deliverReq.clinic_code || deliverReq.clinic_name)}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Ask the clinic staff for the 4-digit PIN shown on their request screen.</p>
            <Input
              value={deliverPin}
              onChange={e => { setDeliverPin(e.target.value.replace(/\D/g, "").slice(0, 4)); setDeliverError(""); }}
              placeholder="••••" maxLength={4} inputMode="numeric" autoFocus
              className="text-center text-2xl tracking-[0.5em] font-bold h-14"
              onKeyDown={e => e.key === "Enter" && submitDeliver()}
              data-testid="deliver-pin-input"
            />
            {deliverError && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="deliver-pin-error">
                <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
                <p className="text-xs text-red-600 font-medium">{deliverError}</p>
              </div>
            )}
            <Button onClick={submitDeliver} disabled={delivering} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="deliver-pin-submit">
              {delivering ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Confirm Delivery"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
